import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { CustomFormItem } from "Components/Form/CustomFormItem";
import { Radio, Input, Form, Row, Col, Checkbox, Select, SelectProps, Space } from "antd";
import { documentsSearchStore } from "../../Stores/DocumentsSearchStore";
import { observer } from "mobx-react";
import { searchPostObjectToRadioButtonValue } from "./utils/searchPostObjectToRadioButtonValue";
import { isEmpty } from "utils/isEmpty";
import { useEffect, useState } from "react";

interface StandardFieldsProps {
  initialData?: API_DocSearchReq;
}

export const StandardFields = observer(({ initialData }: StandardFieldsProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const folderNameDisabled = documentsSearchStore.radioValue === "CURR_DIR";
  const [fileStatusOptions, setFileStatusOptions] = useState([]);
  const [fileOwnersOptions, setFileOwnersOptions] = useState<SelectProps["options"]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchAllFileOwners = async () => {
    try {
      setLoading(true);
      const options = await documentsSearchStore.getAllFileOwnerOptions();
      setFileOwnersOptions(options);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllFileOwners();
    (async () => {
      const options: number[] = await documentsSearchStore.getAllStatusOfFilesInProject();
      setFileStatusOptions(
        options.map((item) => ({
          value: item,
          label: <div className={`status-marker for-filter marker-${item}`} />,
        }))
      );
    })();
  }, []);

  const radioValue = () => {
    if (isEmpty(initialData) || initialData.returnDeleted) {
      return documentsSearchStore.radioValue;
    }
    return searchPostObjectToRadioButtonValue(initialData || {});
  };

  return (
    <Form initialValues={initialData} scrollToFirstError={true} layout="vertical" name="standardFields">
      <CustomFormItem label={t("Search from")} className="search-detail-IE-layout-fix">
        <Radio.Group
          defaultValue={radioValue()}
          onChange={(e) => documentsSearchStore.setRadioValue(e.target.value)}
          options={[
            { label: t("From current dir"), value: "CURR_CHILDREN" },
            { label: t("Only current dir"), value: "CURR_DIR" },
            { label: t("Project"), value: "ALL" },
          ]}
        />
      </CustomFormItem>
      <Row justify="space-between" gutter={16}>
        <Col xs={24} lg={12}>
          <CustomFormItem label={t("Filename")} fullWidth>
            <Form.Item noStyle name="fileName">
              <Input />
            </Form.Item>
          </CustomFormItem>
        </Col>
        <Col xs={24} lg={12}>
          <CustomFormItem label={t("Description")} name="description" fullWidth>
            <Form.Item noStyle name="description">
              <Input />
            </Form.Item>
          </CustomFormItem>
        </Col>
        <Col xs={24} lg={12}>
          <CustomFormItem label={t("Foldername")} name="folderName" fullWidth>
            <Form.Item noStyle name="folderName">
              <Input disabled={folderNameDisabled} />
            </Form.Item>
          </CustomFormItem>
        </Col>
        <Col xs={24} lg={12}>
          <CustomFormItem label={t("File owner")} name="ownerId" fullWidth>
            <Form.Item noStyle name="ownerId">
              <Select
                allowClear={true}
                options={fileOwnersOptions}
                loading={loading}
                showSearch={{
                  filterOption: (input: string, option: SelectProps["options"][number]) =>
                    (option.label as string).toLowerCase().includes(input.toLowerCase()),
                }}
              />
            </Form.Item>
          </CustomFormItem>
        </Col>
        <Col xs={24} lg={12}>
          <CustomFormItem label={t("Index")} name="index" fullWidth>
            <Form.Item noStyle name="index">
              <Input />
            </Form.Item>
          </CustomFormItem>
        </Col>
        <Col xs={24} lg={12}>
          <CustomFormItem label={t("Younger than")} name="youngerThanDays" fullWidth>
            <Form.Item noStyle name="youngerThanDays">
              <Input addonAfter={t("days")} />
            </Form.Item>
          </CustomFormItem>
        </Col>
        <Col xs={24} lg={12}>
          <CustomFormItem label={t("File Status")} name="fileStatus" fullWidth style={{ flexGrow: 1 }}>
            <Form.Item noStyle name="fileStatus">
              <Select options={fileStatusOptions} />
            </Form.Item>
          </CustomFormItem>
        </Col>
        <Col xs={24} lg={12}>
          <Space style={{ width: "100%" }}>
            <CustomFormItem label={" "} fullWidth>
              <Form.Item noStyle name="onlyUnreadFiles" valuePropName="checked">
                <Checkbox>{t("Only unread files")}</Checkbox>
              </Form.Item>
            </CustomFormItem>
            <CustomFormItem label={" "} fullWidth>
              <Form.Item noStyle name="returnDeleted" valuePropName="checked">
                <Checkbox>{t("Show deleted files")}</Checkbox>
              </Form.Item>
            </CustomFormItem>
          </Space>
        </Col>
      </Row>
    </Form>
  );
});
